import { useEffect, useMemo } from 'react';
import { FieldValues, useFormContext, UseFormRegister } from 'react-hook-form';

import RadioGroupButton from '../../../components/form/RadioGroupButton';
import { ContentType, SectionTargetExploreEbookBundleType } from '../../../core/graphql/types';
import {
  EBOOK_TYPE_OPTIONS,
  PRICE_SETTING_OPTIONS,
  RATING_OPTIONS,
  EXPLORE_TYPE_OPTIONS,
  SORT_BY_EXPLORE_BEST_SELLER_OPTIONS,
  SORT_BY_EXPLORE_OPTIONS,
  EBOOK_FILE_TYPE_OPTIONS,
  EXPLORE_TYPE_VALUE,
  CARTOON_TYPE_OPTIONS,
  ISectionTargetType,
  EXPLORE_BUNDLES_TAB_OPTIONS,
  SORT_BY_EXPLORE_BUNDLES_OPTIONS,
  BUNDLES_PRICE_SETTING_OPTIONS,
  BUNDLES_CATEGORY_OPTIONS,
  SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS,
} from '../validates/sectionBuilderSchema';

import DropdownSelectNested from './DropdownSelectNested';
import SelectPintoCategoryWrapper from './SelectPintoCategory';

export type SearchType = 'ebook' | 'user';

export interface IExploreSectionTarget {
  //TODO: continue implements
  register: UseFormRegister<FieldValues>;
  onSelectCategoryId?: (category: number) => void;
}

const ExploreSectionTarget = (props: IExploreSectionTarget) => {
  const { register } = props;

  const { watch, setValue } = useFormContext();
  const [targetType, categoryId, exploreType, sortBy, sortType, contentType] = watch([
    'target.type',
    'target.categoryId',
    'target.exploreType',
    'target.sortBy',
    'target.sortType',
    'contentType',
  ]);

  const exploreTabOptions = useMemo(() => {
    if (targetType === ISectionTargetType.EXPLORE) {
      return EXPLORE_TYPE_OPTIONS;
    }
    if (targetType === ISectionTargetType.EXPLORE_BUNDLES) {
      return EXPLORE_BUNDLES_TAB_OPTIONS;
    }
    return [];
  }, [targetType]);

  const bestSellerSortOptions = useMemo(() => {
    if (targetType === ISectionTargetType.EXPLORE) {
      return SORT_BY_EXPLORE_BEST_SELLER_OPTIONS;
    }
    if (targetType === ISectionTargetType.EXPLORE_BUNDLES) {
      return SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS;
    }
    return [];
  }, [targetType]);

  const sortOptions = useMemo(() => {
    if (targetType === ISectionTargetType.EXPLORE) {
      return SORT_BY_EXPLORE_OPTIONS;
    }
    if (targetType === ISectionTargetType.EXPLORE_BUNDLES) {
      return SORT_BY_EXPLORE_BUNDLES_OPTIONS;
    }
    return [];
  }, [targetType]);

  const priceOptions = useMemo(() => {
    if (targetType === ISectionTargetType.EXPLORE) {
      return PRICE_SETTING_OPTIONS;
    }
    if (targetType === ISectionTargetType.EXPLORE_BUNDLES) {
      return BUNDLES_PRICE_SETTING_OPTIONS;
    }
    return [];
  }, [targetType]);

  useEffect(() => {
    if (!exploreType) {
      setValue('target.exploreType', exploreTabOptions[0].value);
    }
  }, [exploreTabOptions, exploreType, setValue]);

  useEffect(() => {
    // this useEffect for default target.sortBy and target.sortType in case of select Explore as target of Section

    if (!sortBy && !sortType) {
      // in case there are no sortBy and sortType this condition will triggered
      if (exploreType === EXPLORE_TYPE_VALUE.BestSeller || exploreType === SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS) {
        // if exploreType is BestSeller it will default first item from SORT_BY_EXPLORE_BEST_SELLER_OPTIONS to target.sortBy and target.sortType
        bestSellerSortOptions[0].value.forEach((opt) => {
          setValue(`target.${opt.keyName}`, opt.value);
        });
      } else if (exploreType === EXPLORE_TYPE_VALUE.All) {
        // if exploreType is All it will default first item from SORT_BY_EXPLORE_OPTIONS to target.sortBy and target.sortType
        sortOptions[0].value.forEach((opt) => {
          setValue(`target.${opt.keyName}`, opt.value);
        });
      }
      // if exploreType is not All and BestSeller sortBy and sortType will not be default by any options
    } else if (sortBy && sortType) {
      // in case there are target.sortBy and target.sortType have values this condition will triggered
      if (![EXPLORE_TYPE_VALUE.All, EXPLORE_TYPE_VALUE.BestSeller, SectionTargetExploreEbookBundleType.BestSeller].includes(exploreType)) {
        // when exploreType is not All and BestSeller will set sortBy and sortType to null
        setValue('target.sortBy', null);
        setValue('target.sortType', null);
      } else if (
        (exploreType === EXPLORE_TYPE_VALUE.BestSeller || exploreType === SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS) &&
        !bestSellerSortOptions.find((v) => v.value[0].value === sortBy && v.value[1].value === sortType)
      ) {
        // if exploreType is BesSeller and current sortBy and sortType is not choice in SORT_BY_EXPLORE_BEST_SELLER_OPTIONS this will set new default value
        bestSellerSortOptions[0].value.forEach((opt) => {
          setValue(`target.${opt.keyName}`, opt.value);
        });
      } else if (exploreType === EXPLORE_TYPE_VALUE.All && !sortOptions.find((v) => v.value[0].value === sortBy && v.value[1].value === sortType)) {
        // if exploreType is All and current sortBy and sortType is not choice in SORT_BY_EXPLORE_OPTIONS this will set new default value
        sortOptions[0].value.forEach((opt) => {
          setValue(`target.${opt.keyName}`, opt.value);
        });
      }
    }
  }, [bestSellerSortOptions, exploreType, setValue, sortBy, sortOptions, sortType]);

  return (
    <div className="space-y-8 py-8">
      <RadioGroupButton label="Explore Topic" options={exploreTabOptions} register={register} name={`target.exploreType`} />

      {[EXPLORE_TYPE_VALUE.All, EXPLORE_TYPE_VALUE.BestSeller, SectionTargetExploreEbookBundleType.BestSeller].includes(exploreType) && (
        <DropdownSelectNested
          label="SortBy"
          options={exploreType === EXPLORE_TYPE_VALUE.BestSeller || exploreType === SectionTargetExploreEbookBundleType.BestSeller ? bestSellerSortOptions : sortOptions}
          prefixName={`target`}
          name={[`target.sortBy`, 'target.sortType']}
        />
      )}

      {targetType === ISectionTargetType.EXPLORE && (
        <SelectPintoCategoryWrapper
          name={`target.categoryId`}
          categoryId={categoryId}
          disableSelectAll
          selectAllDisabledMessage='Target Type แบบ Explore ต้องเลือกหมวดหมู่แบบเจาะจง จึงไม่รองรับ "เลือกทั้งหมด"'
        />
      )}
      {targetType === ISectionTargetType.EXPLORE_BUNDLES && <RadioGroupButton label="Category" options={BUNDLES_CATEGORY_OPTIONS} register={register} name={`target.categoryId`} />}

      {targetType === ISectionTargetType.EXPLORE && <RadioGroupButton label="Rating" options={RATING_OPTIONS} register={register} name={`target.gteRating`} />}

      <RadioGroupButton isSetArrayValue label="Price Type" options={priceOptions} register={register} name={`target.anyPriceTypes`} />

      {targetType === ISectionTargetType.EXPLORE && (
        <RadioGroupButton isSetArrayValue label="Ebook Type" options={EBOOK_TYPE_OPTIONS} register={register} name={`target.anyTypes`} />
      )}

      {targetType === ISectionTargetType.EXPLORE && (
        <RadioGroupButton isSetArrayValue label="File Type" options={EBOOK_FILE_TYPE_OPTIONS} register={register} name={`target.anyFileTypes`} />
      )}

      {targetType === ISectionTargetType.EXPLORE && contentType === ContentType.Comic ? (
        <RadioGroupButton isSetArrayValue label="Cartoon Type" options={CARTOON_TYPE_OPTIONS} register={register} name={`target.anyWritingTypes`} />
      ) : null}
    </div>
  );
};

export default ExploreSectionTarget;
